import apiService from "./apiService";
import { env } from "../env";

const COMPLETE_VERIFICATION_ENDPOINT = "/social-verification/complete";

// TikTok skips its own login/consent screen for an account that already
// approved this app, which hides the actual "log in and authorize" step from
// a screen recording. disable_auto_auth=1 forces TikTok to show it every
// time. Only needed while recording the app-review demo video — real users
// should get the normal fast re-auth, so flip this back to false afterward.
const FORCE_TIKTOK_REAUTH = false;

const normalizeStatus = (value, fallback) => {
    const normalized = String(value || "").trim().toLowerCase();
    return normalized || fallback;
};

const getCallbackUrl = () => `${window.location.origin}/social-verification/callback`;

const decodeStateValue = (rawState) => {
    const value = String(rawState || "").trim();
    if (!value) return null;

    try {
        return JSON.parse(decodeURIComponent(value));
    } catch {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }
};

export const parseSocialVerificationState = (rawState) => {
    const parsed = decodeStateValue(rawState);

    return {
        platform: String(parsed?.platform || "").trim().toLowerCase(),
        handle: String(parsed?.handle || "").trim(),
        returnTo: String(parsed?.returnTo || "").trim() || "/register",
        // Which account slot (of possibly several for the same platform) this
        // verification belongs to — null means "unknown / older client", the
        // caller falls back to matching by handle text instead.
        index: parsed?.index == null ? null : Number(parsed.index),
        raw: parsed,
    };
};

const buildState = ({ platform, handle, returnTo, index }) =>
    encodeURIComponent(
        JSON.stringify({
            platform: String(platform || "").trim().toLowerCase(),
            handle,
            returnTo: returnTo || "/register",
            index: index == null ? null : Number(index),
        })
    );

const buildProviderAuthUrl = ({ platform, handle, returnTo, index }) => {
    const platformKey = String(platform || "").trim().toLowerCase();
    const callbackUrl = encodeURIComponent(getCallbackUrl());
    const state = buildState({ platform, handle, returnTo, index });

    if (platformKey === "instagram") {
        const clientId = env.REACT_APP_INSTAGRAM_CLIENT_ID || "";
        const scope =
            env.REACT_APP_INSTAGRAM_SCOPE ||
            "instagram_business_basic,instagram_manage_comments,instagram_business_manage_messages";
        const authBase = env.REACT_APP_INSTAGRAM_AUTH_URL || "https://www.instagram.com/oauth/authorize";
        if (!clientId || !authBase) return "";

        return `${authBase}?client_id=${encodeURIComponent(clientId)}&redirect_uri=${callbackUrl}&response_type=code&scope=${encodeURIComponent(scope)}&state=${state}&enable_fb_login=0&force_authentication=1`;
    }

    if (platformKey === "tiktok") {
        const clientId = env.REACT_APP_TIKTOK_CLIENT_ID || "";
        const scope = env.REACT_APP_TIKTOK_SCOPE || "user.info.basic";
        const authBase = env.REACT_APP_TIKTOK_AUTH_URL || "https://www.tiktok.com/v2/auth/authorize/";
        if (!clientId || !authBase) return "";

        const forceReauth = FORCE_TIKTOK_REAUTH ? "&disable_auto_auth=1" : "";
        return `${authBase}?client_key=${encodeURIComponent(clientId)}&redirect_uri=${callbackUrl}&response_type=code&scope=${encodeURIComponent(scope)}&state=${state}${forceReauth}`;
    }

    return "";
};


export const isPlatformVerifiable = (platformKey) => {
    if (platformKey === "instagram") return Boolean(env.REACT_APP_INSTAGRAM_CLIENT_ID);
    if (platformKey === "tiktok") return Boolean(env.REACT_APP_TIKTOK_CLIENT_ID);
    return false;
};

export const startSocialVerification = async ({ platform, handle, profileUrl, returnTo, index }) => {
    const platformKey = String(platform || "").trim().toLowerCase();
    const directAuthUrl = buildProviderAuthUrl({ platform: platformKey, handle, returnTo, index });
    if (directAuthUrl) {
        return {
            success: true,
            status: "redirecting",
            message: "",
            redirectUrl: directAuthUrl,
        };
    }

    return {
        success: false,
        status: "unsupported",
        message: "Missing OAuth configuration for this platform.",
        redirectUrl: "",
    };
};

export const completeSocialVerification = async ({ platform, code, state }) => {
    try {
        const response = await apiService.post(COMPLETE_VERIFICATION_ENDPOINT, {
            p_platform: platform,
            p_code: code,
            p_state: state || "",
        });
        const data = response?.data || {};
        return {
            success: true,
            status: normalizeStatus(data?.status || data?.Status, "verified"),
            message: data?.message || data?.Message || "",
            handle: data?.handle || data?.Handle || "",
            redirectTo: data?.redirectTo || data?.RedirectTo || "",
        };
    } catch (error) {
        if (error?.response?.status === 404) {
            return {
                success: false,
                status: "unsupported",
                message: "Backend completion endpoint is not available yet.",
                handle: "",
                redirectTo: "",
            };
        }

        return {
            success: false,
            status: "error",
            message: error?.response?.data?.message || error?.message || "Unable to complete social verification.",
            handle: "",
            redirectTo: "",
        };
    }
};
